import { FC, useRef } from 'react';
import { DevicePreviewContext, DevicePreviewContextState } from './DevicePreviewContext';
import { useGetDevicePreviews } from '../hooks/useGetDevicePreviews';
import { useQueryParamContext } from 'modules/templateEditor/context/QueryParamContext';
import { DEFAULT_CATEGORY } from '../consts';
import { DevicePreview, DevicePreviewStatus } from '../types';
import { useSelector } from 'react-redux';
import { selectIsDisplayEmailOnAcidPreviewsFeatureFlagOn } from 'core/userContext/selectors';
import { useGetTemplate } from '../hooks/useGetTemplate';
import { MessageMedium } from 'constants/messageMediums';

const REFETCH_INTERVAL = 5000;
const MAX_FETCH_COUNT = 6;
const DEFAULT_LOCALE = 'Default';

export const DevicePreviewContextProvider: FC<React.PropsWithChildren<unknown>> = ({
  children,
}) => {
  const { templateId, locale } = useQueryParamContext();
  const isFFOn = useSelector(selectIsDisplayEmailOnAcidPreviewsFeatureFlagOn);
  const localeKey = locale || DEFAULT_LOCALE;
  const fetchCountRef = useRef({ [localeKey]: 0 });

  const { template, isLoading: isLoadingTemplate } = useGetTemplate({
    variables: {
      id: templateId,
    },
    skip: !isFFOn || !templateId,
  });

  const isEmailPreview = template?.messageMedium === MessageMedium.Email;

  const { previews, isLoading, refetch } = useGetDevicePreviews(
    { templateId, locale: locale || undefined },
    {
      enabled: isEmailPreview,
      refetchInterval: (previews: Record<string, DevicePreview>) =>
        (fetchCountRef.current[localeKey] || 0) < MAX_FETCH_COUNT &&
        previews &&
        typeof previews === 'object' &&
        Object.values(previews).some(({ status }) => status !== DevicePreviewStatus.Complete)
          ? REFETCH_INTERVAL
          : false,
      onSuccess: () => {
        fetchCountRef.current = { [localeKey]: (fetchCountRef.current[localeKey] || 0) + 1 };
      },
    },
  );

  const categoriesCount = previews.reduce(
    (acc, { category }) => {
      acc[category] = (acc[category] || 0) + 1;
      return acc;
    },
    { [DEFAULT_CATEGORY]: previews.length } as Record<string, number>,
  );

  const context: DevicePreviewContextState = {
    devicePreviews: previews,
    categoriesCount,
    isLoading: isLoading || isLoadingTemplate,
    refetch: () => {
      fetchCountRef.current = { [localeKey]: 0 };
      refetch();
    },
    isEnabled: isFFOn,
    templateName: template?.name,
    isTemplateError: isFFOn && !isLoadingTemplate && !template,
  };

  return <DevicePreviewContext.Provider value={context}>{children}</DevicePreviewContext.Provider>;
};
